import type { ComponentType } from 'react';
import {
  AppstoreOutlined,
  CarOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  AreaChartOutlined,
  GiftOutlined,
  LayoutOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
  ProfileOutlined,
  SettingOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { getErpRouteIcon } from '@erp/navigation/erpIcons.tsx';

export type Domain = 'eshop' | 'eshopDashboard' | 'erp' | 'erpDashboard' | 'cms' | 'public';

export type DashboardNavGroup = 'general' | 'eshop' | 'cms' | 'erp';

export type ErpFunctionalGroup =
  | 'overview'
  | 'ventas'
  | 'stock'
  | 'compras'
  | 'servicios'
  | 'suscripciones'
  | 'rrhh'
  // | 'gastos'
  | 'finanzas'
  | 'configuracion'
  | 'clientes'
  ;

export interface PageRoute {
  key: string;
  path: string;
  title: string;
  domain: Domain;
  icon?: ComponentType;
  description?: string;
  navGroup?: DashboardNavGroup;
  group?: ErpFunctionalGroup;
  colorToken?: string;
  featureFlag?: string;
  requiredApps?: readonly string[];
  requiredAnyApps?: readonly string[];
  requiredCapabilities?: readonly string[];
  requiredPermissions?: readonly string[];
  requiredGroups?: readonly string[];
  requiresStaff?: boolean;
  enabled?: boolean;
  featureFlagEnabledCopy?: string;
  priority?: number;
  advancedMode?: boolean; // Si true, solo se muestra en modo avanzado
  allowSuperuserWithoutApp?: boolean;
}

const ERP_REQUIRED_APPS = ['erp'] as const;
const ESHOP_REQUIRED_APPS = ['eshop'] as const;
const CMS_REQUIRED_APPS = ['cms'] as const;
const CRM_REQUIRED_APPS = ['crm'] as const;

const generalRoutes: PageRoute[] = [
  {
    key: 'dashboard.overview',
    title: 'Panel de Tienda',
    description: 'Resumen de métricas clave y actividad reciente.',
    path: '/eshop/dashboard',
    domain: 'eshopDashboard',
    icon: DashboardOutlined,
    navGroup: 'general',
    colorToken: 'blue-7',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 1,
  },
  {
    key: 'dashboard.settings',
    title: 'Configuración',
    description: 'Personalizá branding, dominios y preferencias globales.',
    path: '/eshop/dashboard/settings',
    domain: 'eshopDashboard',
    icon: SettingOutlined,
    navGroup: 'general',
    colorToken: 'blue-8',
    requiredGroups: ['admin'],
    requiresStaff: true,
    priority: 2,
  },
  {
    key: 'control.sites',
    title: 'Control de sitios',
    description: 'Administracion interna temporal de sitios y planes.',
    path: '/control/sites',
    domain: 'erpDashboard',
    icon: SafetyCertificateOutlined,
    navGroup: 'general',
    colorToken: 'blue-9',
    requiredApps: ['av_control'],
    requiresStaff: true,
    priority: 3,
  },
];

const eshopDashboardRoutes: PageRoute[] = [
  {
    key: 'eshop.catalogue',
    title: 'Items',
    description:
      'Administrá productos, servicios, variantes y categorías del e‑shop.',
    path: '/eshop/dashboard/items/overview',
    domain: 'eshopDashboard',
    icon: AppstoreOutlined,
    navGroup: 'eshop',
    colorToken: 'cyan-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiredPermissions: ['items:read', 'items:read_own'],
    requiresStaff: true,
    priority: 1,
  },
  {
    key: 'eshop.orders',
    title: 'Órdenes',
    description: 'Seguimiento de pedidos y estados de fulfillment.',
    path: '/eshop/dashboard/orders',
    domain: 'eshopDashboard',
    icon: ProfileOutlined,
    navGroup: 'eshop',
    colorToken: 'orange-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 2,
  },
  {
    key: 'eshop.stats',
    title: 'Estadísticas',
    description:
      'KPIs de clientes, carritos y órdenes con productos destacados.',
    path: '/eshop/dashboard/stats',
    domain: 'eshopDashboard',
    icon: AreaChartOutlined,
    navGroup: 'eshop',
    colorToken: 'blue-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 3,
  },
  {
    key: 'eshop.payments',
    title: 'Pagos',
    description: 'Configurá métodos de cobro y conciliación.',
    path: '/eshop/dashboard/payments/overview',
    domain: 'eshopDashboard',
    icon: CreditCardOutlined,
    navGroup: 'eshop',
    colorToken: 'green-6',
    requiredApps: [...ESHOP_REQUIRED_APPS, 'eshop__payments'],
    requiresStaff: true,
    priority: 4,
  },
  {
    key: 'eshop.shipping',
    title: 'Envíos',
    description: 'Definí reglas de logística, costos y transportistas.',
    path: '/eshop/dashboard/shipping/overview',
    domain: 'eshopDashboard',
    icon: CarOutlined,
    navGroup: 'eshop',
    colorToken: 'purple-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 5,
  },
  {
    key: 'eshop.offers',
    title: 'Ofertas y cupones',
    description: 'Gestioná campañas, descuentos y cupones promocionales.',
    path: '/eshop/dashboard/offers/overview',
    domain: 'eshopDashboard',
    icon: GiftOutlined,
    navGroup: 'eshop',
    colorToken: 'magenta-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiredPermissions: ['eshop:offers:view'],
    requiresStaff: true,
    priority: 6,
  },
  {
    key: 'eshop.banners',
    title: 'Banners',
    description: 'Administrá banners promocionales y hero.',
    path: '/eshop/dashboard/banners/overview',
    domain: 'eshopDashboard',
    icon: PictureOutlined,
    navGroup: 'eshop',
    colorToken: 'gold-6',
    requiredApps: [...ESHOP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 7,
  },
];

const cmsRoutes: PageRoute[] = [
  {
    key: 'cms.siteBuilder',
    title: 'Editor de sitio',
    description: 'Edita las páginas web con el modelo legacy migrado.',
    path: '/site-builder',
    domain: 'cms',
    icon: LayoutOutlined,
    navGroup: 'cms',
    colorToken: 'purple-6',
    featureFlag: 'cms',
    requiredApps: [...CMS_REQUIRED_APPS],
    requiresStaff: true,
    priority: 1,
  },
  {
    key: 'cms.legacySiteBuilder',
    title: 'Editor legacy web',
    description: 'Edita páginas migradas desde esquema_web con preview completo.',
    path: '/legacy-site-builder',
    enabled: false,
    domain: 'cms',
    icon: LayoutOutlined,
    navGroup: 'cms',
    colorToken: 'purple-7',
    featureFlag: 'cms',
    requiredApps: [...CMS_REQUIRED_APPS],
    requiresStaff: true,
    priority: 2,
  },
];

const erpDashboardRoutes: PageRoute[] = [
  {
    key: 'erp.overview',
    title: 'Overview ERP',
    description: 'Accesos principales del ERP agrupados por módulo.',
    path: '/erp',
    domain: 'erpDashboard',
    icon: DashboardOutlined,
    navGroup: 'erp',
    group: 'overview',
    colorToken: 'gold-9',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiresStaff: true,
    priority: 1,
  },
  {
    key: 'erp.sales.pos',
    title: 'Venta directa',
    description: 'Opera cajas y ventas presenciales desde el POS.',
    path: '/erp/sale',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.sales.pos'),
    navGroup: 'erp',
    group: 'ventas',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__sales'],
    requiredPermissions: ['sale_orders:read', 'sale_payments:add'],
    requiresStaff: true,
    priority: 2,
  },
  {
    key: 'erp.orders.list',
    title: 'Órdenes',
    description: 'Controlá el pipeline completo de pedidos ERP.',
    path: '/erp/orders',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.orders.list'),
    navGroup: 'erp',
    group: 'ventas',
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS], // 'erp__sales',
    requiredAnyApps: ['erp__sales', 'erp__payments'],
    requiredPermissions: [
      'orders:view',
      'sale_orders:read',
      'sale_orders:write',
      'sale_orders:cancel',
      'commissions:read',
    ],
    requiresStaff: true,
    priority: 3,
  },
  {
    key: 'erp.quotes',
    title: 'Presupuestos',
    description:
      'Gestiona propuestas comerciales, links publicos y conversion a venta.',
    path: '/erp/quotes',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.orders.list'),
    navGroup: 'erp',
    group: 'ventas',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__sales__quotes'],
    requiredPermissions: ['quotes:read'],
    requiresStaff: true,
    priority: 3.2,
  },
  {
    key: 'erp.sales.offers',
    title: 'Ofertas y cupones',
    description: 'Gestiona promociones y cupones disponibles para ventas ERP.',
    path: '/erp/offers/overview',
    domain: 'erpDashboard',
    icon: GiftOutlined,
    navGroup: 'erp',
    group: 'ventas',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__sales__offers'],
    requiredPermissions: ['eshop:offers:view'],
    requiresStaff: true,
    advancedMode: true,
    priority: 3.3,
  },
  // {
  //   key: 'erp.orders.carts',
  //   title: 'Carritos abiertos',
  //   description: 'Supervisá carritos activos y recuperá ventas.',
  //   path: '/erp/orders?tab=carts',
  //   domain: 'erpDashboard',
  //   icon: getErpRouteIcon('erp.orders.carts'),
  //   navGroup: 'erp',
  //   group: 'ventas',
  //   colorToken: 'gold-5',
  //   featureFlag: 'erp',
  //   requiredApps: [...ERP_REQUIRED_APPS],
  //   requiredPermissions: ['carts:view'],
  //   requiresStaff: true,
  //   priority: 4,
  // },
  // {
  //   key: 'erp.orders.returns',
  //   title: 'Devoluciones',
  //   description: 'Gestioná devoluciones y notas de crédito ERP.',
  //   path: '/erp/orders?tab=returns',
  //   domain: 'erpDashboard',
  //   icon: getErpRouteIcon('erp.orders.returns'),
  //   navGroup: 'erp',
  //   group: 'ventas',
  //   colorToken: 'gold-4',
  //   featureFlag: 'erp',
  //   requiredApps: [...ERP_REQUIRED_APPS],
  //   requiredPermissions: ['returns:view'],
  //   requiresStaff: true,
  //   enabled: false,
  //   featureFlagEnabledCopy: 'Falta exponer módulo de devoluciones en frontend.',
  //   priority: 5,
  // },
  {
    key: 'erp.inventory',
    title: 'Control de Stock',
    description: 'Visualizá existencias y lotes por almacén.',
    path: '/erp/inventory',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.inventory'),
    navGroup: 'erp',
    group: 'stock',
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__stock'],
    requiredPermissions: ['inventory:read'],
    requiresStaff: true,
    priority: 6,
  },
  {
    key: 'erp.inventory.moves',
    title: 'Movimientos',
    description: 'Seguimiento de movimientos y transferencias.',
    path: '/erp/inventory?tab=moves',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.inventory.moves'),
    navGroup: 'erp',
    group: 'stock',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__stock'],
    requiredPermissions: ['inventory:read'],
    requiresStaff: true,
    priority: 7,
  },
  {
    key: 'erp.inventory.adjustments',
    title: 'Ajustes',
    description: 'Regularizá stock con ajustes documentados.',
    path: '/erp/inventory?tab=adjustments',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.inventory.adjustments'),
    navGroup: 'erp',
    group: 'stock',
    colorToken: 'gold-4',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__stock'],
    requiredPermissions: ['inventory:read'],
    requiresStaff: true,
    priority: 8,
  },
  {
    key: 'erp.inventory.byWarehouse',
    title: 'Inventario por almacén',
    description: 'Analizá saldos por almacén y ubicación.',
    path: '/erp/inventory?tab=stock',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.inventory.byWarehouse'),
    navGroup: 'erp',
    group: 'stock',
    colorToken: 'gold-8',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__stock'],
    requiredPermissions: ['inventory:read'],
    requiresStaff: true,
    enabled: true,
    priority: 9,
  },
  {
    key: 'erp.purchasing.orders',
    title: 'Órdenes de compra',
    description: 'Planificá y seguí compras a proveedores.',
    path: '/erp/purchasing/purchase-orders',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.purchasing.orders'),
    navGroup: 'erp',
    group: 'compras',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredAnyApps: ['erp__purchasing', 'erp__stock', 'erp__expenses', 'erp__cashflow'],
    requiredPermissions: ['purchasing:read'],
    requiresStaff: true,
    priority: 9,
  },
  {
    key: 'erp.suppliers',
    title: 'Proveedores',
    description: 'Administrá proveedores y datos de contacto.',
    path: '/erp/suppliers',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.suppliers'),
    navGroup: 'erp',
    group: 'compras',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredAnyApps: ['erp__purchasing', 'erp__stock', 'erp__expenses', 'erp__cashflow'],
    requiredPermissions: ['suppliers:read'],
    requiresStaff: true,
    priority: 10,
    advancedMode: true,
  },
  {
    key: 'erp.services.agenda',
    title: 'Agenda de Turnos',
    description: 'Asigná turnos y calendarizá servicios.',
    path: '/erp/appointments',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.agenda'),
    navGroup: 'erp',
    group: 'servicios',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__appointments'],
    requiredPermissions: ['appointments:read', 'appointments:write'],
    requiresStaff: true,
    priority: 11,
  },
  {
    key: 'erp.services.group_classes',
    title: 'Clases grupales',
    description: 'Administra grupos, cupos y operacion de clases.',
    path: '/erp/appointments/classes',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.group_classes'),
    navGroup: 'erp',
    group: 'servicios',
    colorToken: 'gold-6',
    featureFlag: 'erp__appointments__groups',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__appointments__groups'],
    requiredPermissions: [
      'appointments:groups:read',
      'appointments:groups:write',
    ],
    requiresStaff: true,
    priority: 11.2,
  },
  {
    key: 'erp.services.clinical-history',
    title: 'Historias Clínicas',
    description: 'Buscar, revisar y registrar información clínica de clientes.',
    path: '/erp/clinical-history',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.clinical-history'),
    navGroup: 'erp',
    group: 'servicios',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__appointments__history'],
    requiredPermissions: [
      'appointments:history:read',
      'appointments:history:write',
      'appointments:history:write_own',
    ],
    requiresStaff: true,
    priority: 11.5,
  },
  // {
  //   key: 'erp.services.catalogue',
  //   title: 'Servicios disponibles',
  //   description: 'Gestioná asignaciones y disponibilidad por servicio.',
  //   path: '/erp/services/catalogue',
  //   domain: 'erpDashboard',
  //   icon: getErpRouteIcon('erp.services.catalogue'),
  //   navGroup: 'erp',
  //   group: 'servicios',
  //   colorToken: 'gold-6',
  //   featureFlag: 'erp',
  //   requiredApps: [...ERP_REQUIRED_APPS],
  //   requiredPermissions: ['appointments:read'],
  //   requiresStaff: true,
  //   priority: 12,
  // },
  {
    key: 'erp.services.itemEditor',
    title: 'Items',
    description: 'Creá o actualizá productos y servicios desde el ERP.',
    path: '/erp/services/catalogue',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.itemEditor'),
    navGroup: 'erp',
    group: 'stock',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    // requiredAnyApps: ['erp__appointments', 'erp__stock'],
    requiredPermissions: ['items:read', 'items:read_own'],
    requiresStaff: true,
    priority: 12.5,
    advancedMode: true,
  },
  {
    key: 'erp.services.spaces',
    title: 'Sectores y espacios',
    description: 'Definí los sectores donde se prestan tus servicios.',
    path: '/erp/services/spaces',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.spaces'),
    navGroup: 'erp',
    group: 'servicios',
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__appointments'],
    requiredPermissions: ['spaces:write'],
    requiresStaff: true,
    priority: 13,
    advancedMode: true,
  },

  {
    key: 'erp.rrhh.staff',
    title: 'Staff',
    description: 'Gestiona staff y convenios bajo RRHH.',
    path: '/erp/rrhh/staff',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.professionals'),
    navGroup: 'erp',
    group: 'rrhh',
    colorToken: 'gold-4',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__staff'],
    requiredPermissions: ['roles:manage'],
    requiresStaff: true,
    priority: 14.05,
    advancedMode: true,
  },
  {
    key: 'erp.rrhh.agreements',
    title: 'Convenios RRHH',
    description: 'Administra sueldos fijos y comisiones del staff.',
    path: '/erp/rrhh/agreements',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.commissions'),
    navGroup: 'erp',
    group: 'rrhh',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__rrhh'],
    requiredPermissions: ['rrhh:agreements:read'],
    requiresStaff: true,
    priority: 14.06,
    advancedMode: true,
  },
  {
    key: 'erp.rrhh.settlements',
    title: 'Liquidaciones RRHH',
    description:
      'Genera liquidaciones, aprueba comisiones y registra pagos al staff.',
    path: '/erp/rrhh/settlements',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.commissions'),
    navGroup: 'erp',
    group: 'rrhh',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__rrhh'],
    requiredPermissions: ['rrhh:settlements:read'],
    requiresStaff: true,
    priority: 14.07,
    advancedMode: true,
  },
  /**
   * TODO: RESERVADO PARA PRÓXIMA ITERACIÓN
  {
    key: 'erp.services.history',
    title: 'Historial de servicios',
    description: 'Consultá servicios completados y pendientes.',
    path: '/erp/appointments?view=history',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.services.history'),
    group: 'servicios',
    colorToken: 'gold-4',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__expenses'],
    requiredPermissions: ['appointments:read', 'appointments:history'],
    requiresStaff: true,
    enabled: false,
    featureFlagEnabledCopy: 'Historial de servicios pendiente de publicar.',
  },*/
  // ===== SUSCRIPCIONES =====
  {
    key: 'erp.subscriptions.plans',
    title: 'Planes de Suscripción',
    description: 'Creá y gestioná planes con servicios incluidos y precios.',
    path: '/erp/subscriptions/plans',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.subscriptions.plans'),
    navGroup: 'erp',
    group: 'suscripciones',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__subscriptions'],
    requiredPermissions: ['subscriptions:plans:read'],
    requiresStaff: true,
    priority: 14.1,
    advancedMode: true,
  },
  {
    key: 'erp.subscriptions.customers',
    title: 'Suscripciones de Clientes',
    description: 'Visualizá y administra suscripciones activas por cliente.',
    path: '/erp/subscriptions/customers',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.subscriptions.customers'),
    navGroup: 'erp',
    group: 'suscripciones',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__subscriptions'],
    requiredPermissions: ['subscriptions:read'],
    requiresStaff: true,
    priority: 14.2,
  },
  {
    key: 'erp.expenses',
    title: 'Gastos',
    description: 'Registrá egresos y conciliá comprobantes ERP.',
    path: '/erp/expenses',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.expenses'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__expenses'],
    requiredPermissions: ['expenses:read'],
    requiresStaff: true,
    priority: 12,
  },
  {
    key: 'erp.expenses.paymentMethods',
    title: 'Medios de pago',
    description: 'Configurá medios de cobro y sus comisiones en el ERP.',
    path: '/erp/payments/methods',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.expenses.paymentMethods'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__payments'],
    requiredPermissions: ['erp:payments'],
    requiresStaff: true,
    priority: 13,
    advancedMode: true,
  },
  {
    key: 'erp.commissions',
    title: 'Comisiones',
    description: 'Seguimiento de comisiones por staff y ventas ERP.',
    path: '/erp/commissions',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.commissions'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__rrhh'],
    requiredPermissions: ['commissions:read', 'commissions:read_own'],
    requiresStaff: true,
    priority: 13.1,
    advancedMode: true,
  },
  {
    key: 'erp.payments.cashboxes',
    title: 'Cajas',
    description: 'Administra cajas fisicas y virtuales para cobros y pagos.',
    path: '/erp/payments/cashboxes',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.expenses.paymentMethods'),
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredAnyApps: ['erp__cashflow', 'erp__cash_session'],
    requiredPermissions: ['erp:payments', 'cashboxes:read'],
    requiresStaff: true,
    priority: 13.2,
    advancedMode: true,
  },
  {
    key: 'erp.cashSession',
    title: 'Sesion de caja',
    description: 'Apertura, cierre y autorizacion operativa de caja.',
    path: '/erp/cash-session',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.cashflow.balance'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-8',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__cash_session'],
    requiredPermissions: ['cashboxes:read', 'cashboxes:close'],
    requiresStaff: true,
    priority: 13.4,
    advancedMode: true,
  },
  // {
  //   key: 'erp.finance.balances',
  //   title: 'Balances y cierres',
  //   description: 'Controlá créditos aplicados y deuda pendiente por cliente.',
  //   path: '/erp/finance/balances',
  //   domain: 'erpDashboard',
  //   icon: getErpRouteIcon('erp.finance.balances'),
  //   navGroup: 'erp',
  //   group: 'finanzas',
  //   colorToken: 'gold-8',
  //   featureFlag: 'erp',
  //   requiredApps: [...ERP_REQUIRED_APPS],
  //   requiredPermissions: ['erp:balances'],
  //   requiresStaff: true,
  //   priority: 14,
  // },
  {
    key: 'erp.cashflow.balance',
    title: 'Flujos de Caja',
    description:
      'Seguimiento de cajas físicas y virtuales con filtros y exportes CSV.',
    path: '/erp/cashflow/balance',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.cashflow.balance'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__cashflow'],
    requiredPermissions: ['cashflow:view'],
    requiresStaff: true,
    priority: 15,
  },
  /**
   * TODO: Reservado para la próxima iteración
  {
    key: 'erp.expenses.closings',
    title: 'Cierres',
    description: 'Realizá cierres de caja y conciliaciones.',
    path: '/erp/expenses?view=closing',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.expenses.closings'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...CRM_REQUIRED_APPS],
    requiredPermissions: ['expenses:read'],
    requiresStaff: true,
    enabled: true,
    featureFlagEnabledCopy: 'Pantalla de cierres ERP aún no disponible.',
  },*/
  {
    key: 'erp.configuration.parameters',
    title: 'Configuración',
    description: 'Configura ajustes generales del ERP.',
    path: '/erp/configuration/parameters',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.parameters'),
    group: 'configuracion',
    colorToken: 'gold-7',
    navGroup: 'erp',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredPermissions: ['settings:write', 'roles:manage'],
    requiredGroups: ['admin'],
    requiresStaff: true,
    enabled: true,
    featureFlagEnabledCopy:
      'Configuración específica ERP pendiente de exponer.',
    advancedMode: true,
  },
  /**
   * TODO: RESERVADO PARA PRÓXIMA ITERACIÓN
  {
    key: 'erp.configuration.commissions',
    title: 'Comisiones',
    description: 'Define acuerdos por porcentaje o monto y asigná agentes sugeridos.',
    path: '/erp/configuration/commissions',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.commissions'),
    navGroup: 'erp',
    group: 'finanzas',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS, 'erp__rrhh'],
    requiredPermissions: ['pos:commissions:read', 'commissions:read_own'],
    requiresStaff: true,
    priority: 16,
  },
    */
  /**
   * TODO: RESERVADO PARA PRÓXIMA ITERACIÓN
  {
    key: 'erp.configuration.roles',
    title: 'Roles y permisos',
    description: 'Administrá roles internos y accesos ERP.',
    path: '/erp/configuration/roles',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.configuration.roles'),
    navGroup: 'erp',
    group: 'configuracion',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredPermissions: ['erp:roles:manage'],
    requiresStaff: true,
    enabled: true,
    priority: 17,
  },
  */
  // CRM
  {
    key: 'crm.customers.overview',
    title: 'Clientes',
    description: 'Panel de visualización general de clientes',
    path: '/erp/customers/overview',
    domain: 'erpDashboard',
    icon: getErpRouteIcon('erp.customers.overview'),
    navGroup: 'erp',
    group: 'clientes',
    colorToken: 'gold-5',
    featureFlag: 'erp',
    requiredApps: [...ERP_REQUIRED_APPS],
    requiredAnyApps: ['crm__customers'],
    requiredPermissions: ['clients:read'],
    requiresStaff: true,
    enabled: true,
    priority: 1.5,
  },
  {
    key: 'erp.communication.reminders',
    title: 'Recordatorios',
    description: 'Gestioná plantillas y envíos de WhatsApp para turnos.',
    path: '/erp/communication/reminders',
    domain: 'erpDashboard',
    icon: MessageOutlined,
    navGroup: 'erp',
    group: 'clientes',
    colorToken: 'gold-6',
    featureFlag: 'erp',
    requiredApps: [...CRM_REQUIRED_APPS],
    requiredAnyApps: ['crm__remainders'],
    requiredPermissions: ['conns:reminders:read'],
    requiresStaff: true,
    priority: 1.6,
  },
  {
    key: 'crm.chat',
    title: 'Chat en vivo',
    description:
      'Visualizá y respondé conversaciones de WhatsApp en tiempo real.',
    path: '/crm/chat',
    domain: 'erpDashboard',
    icon: MessageOutlined,
    navGroup: 'erp',
    group: 'clientes',
    colorToken: 'gold-7',
    featureFlag: 'erp',
    requiredApps: [...CRM_REQUIRED_APPS],
    requiredAnyApps: ['crm__chat'],
    requiredPermissions: ['crm:chat'],
    requiresStaff: true,
    priority: 1.7,
  },
  {
    key: 'crm.forms.overview',
    title: 'Formularios',
    description: 'Creá, publicá y asigná formularios personalizados.',
    path: '/crm/forms/overview',
    domain: 'erpDashboard',
    icon: ProfileOutlined,
    navGroup: 'erp',
    group: 'clientes',
    colorToken: 'gold-7',
    requiredApps: ['crm__forms'],
    requiredAnyApps: ['crm__forms__builder'],
    requiredCapabilities: ['crm.forms.manage'],
    requiredPermissions: ['forms:definitions:admin', 'forms:assignments:write'],
    requiredGroups: ['admin'],
    requiresStaff: true,
    allowSuperuserWithoutApp: true,
    enabled: true,
    priority: 1.8,
  },
];

export const pageRoutes: PageRoute[] = [
  ...generalRoutes,
  ...eshopDashboardRoutes,
  ...cmsRoutes,
  ...erpDashboardRoutes,
];

export const ERP_PERMISSION_KEYS: ReadonlySet<string> = new Set(
  pageRoutes
    .filter((route) => route.navGroup === 'erp' && route.requiredPermissions?.length)
    .flatMap((route) => route.requiredPermissions ?? []),
);

export const isErpPermission = (permission: string): boolean =>
  permission.startsWith('erp:') || ERP_PERMISSION_KEYS.has(permission);

export default pageRoutes;
